import { useEffect, useState } from 'react'
import { Navigate, Outlet, Route, Routes, useNavigate } from 'react-router-dom'

import { setAvailableAuthDispatch } from './api/httpClient.ts'
import { OrderExtendedProfile } from './api/services/orders/types.ts'
import RedirectIfAuth from './components/guards/RedirectIfAuth/RedirectIfAuth.tsx'
import RequireAuth from './components/guards/RequireAuth/RequireAuth.tsx'
import EppReportPage from './components/invoices/EPPReportPage.tsx'
import { useAuth } from './context/auth'
import useColorMode from './hooks/useColorMode.tsx'
import { usePanelUserRights } from './hooks/usePanelUserRights.ts'
import useStripeAccountInfo from './hooks/useStripeAccountInfo.ts'
import DefaultLayout from './layout/DefaultLayout'
import Account from './pages/account/Account.tsx'
import AssignmentCodeDetails from './pages/application/AssignmentCodeDetails.tsx'
import AssignmentCodes from './pages/application/AssignmentCodes.tsx'
import CareGiver from './pages/application/CareGiver.tsx'
import SurveyTemplateReport from './pages/application/SurveyTemplateReport.tsx'
import DietBase from './pages/daymenus/DietBase.tsx'
import Login from './pages/Login.tsx'
import NotFound from './pages/NotFound.tsx'
import OrderDetails from './pages/orders/OrderDetails.tsx'
import OrderRealization from './pages/orders/OrderRealization.tsx'
import Orders from './pages/orders/Orders.tsx'
import CreatePatientProfile from './pages/patientProfiles/CreatePatientProfile.tsx'
import PatientBodyMeasurementsDetails from './pages/patientProfiles/PatientBodyMeasurementsDetails.tsx'
import PatientProfileDetails from './pages/patientProfiles/PatientProfileDetails.tsx'
import PatientProfilesList from './pages/patientProfiles/PatientProfilesList.tsx'
import PatientSurveyDetails from './pages/patientProfiles/PatientSurveyDetails.tsx'
import Balance from './pages/payments/Balance.tsx'
import { Invoices } from './pages/payments/Invoices.tsx'
import Payments from './pages/payments/Payments.tsx'
import PriceList from './pages/payments/PriceList.tsx'
import Statistics from './pages/payments/Statistics.tsx'
import StripeAccount from './pages/payments/StripeAccount.tsx'
import Transactions from './pages/payments/Transactions.tsx'
import Settings from './pages/Settings'
import ChangePassword from './pages/settings/ChangePassword.tsx'
import SubAccounts from './pages/subaccounts/SubAccounts.tsx'
import { isUserExistsInGroup } from './utils/userRights.ts'

const App = () => {
  const {} = useColorMode()
  const { state, dispatch } = useAuth()
  const navigate = useNavigate()
  const url = new URL(window.location.href)
  const { hasUserRights } = usePanelUserRights()
  const isRestrictedUser = isUserExistsInGroup(
    state.groups,
    'dd_panel_restricted_users'
  )
  const isCertificateRequiredUser = isUserExistsInGroup(
    state.groups,
    'certificate_required_users_group_name'
  )
  const hasMainUserRights = isUserExistsInGroup(
    state.groups,
    'dd_panel_main_users'
  )
  const userFromPD3 = isUserExistsInGroup(state.groups, 'pd3_users')
  const { stripeAccountCompleted } = useStripeAccountInfo()
  const errorsFromPD3 = url.searchParams.get('error')
  const [errorCode, setErrorCode] = useState('')

  const checkErrorsFromPD3 = () => {
    if (errorsFromPD3 !== null) {
      setErrorCode(errorsFromPD3)
    }
  }

  const handleAuthToken = () => {
    const accessToken = url.searchParams.get('access_token')
    const refreshToken = url.searchParams.get('refresh_token')
    const email = url.searchParams.get('email')
    const groups = url.searchParams.get('groups')

    if (accessToken !== null && refreshToken !== null && email !== null) {
      dispatch({
        type: 'LOGIN',
        payload: {
          accessToken,
          refreshToken,
          email,
          groups: groups ? groups.split(',') : []
        }
      })

      const redirectAfterLogin = localStorage.getItem('redirectAfterLogin')
      if (redirectAfterLogin && redirectAfterLogin !== '/') {
        navigate(redirectAfterLogin, {
          state: { from: redirectAfterLogin },
          replace: true
        })
      } else {
        navigate('/', { replace: true })
      }
    }
  }

  useEffect(() => {
    if (
      !url.searchParams.get('access_token') &&
      window.location.pathname !== '/login' &&
      window.location.pathname !== '/orders' &&
      window.location.pathname !== '/'
    ) {
      const isRedirectAfterLogin =
        localStorage.getItem('redirectAfterLogin') !== null &&
        localStorage.getItem('redirectAfterLogin') !== '/'
      if (isRedirectAfterLogin) return

      const redirectAfterLogin = window.location.pathname
      localStorage.setItem('redirectAfterLogin', redirectAfterLogin)
    }
  }, [url, window.location.pathname])

  useEffect(() => {
    checkErrorsFromPD3()
    setAvailableAuthDispatch(dispatch)
    handleAuthToken()
  }, [])

  const redirectAfterLogin = () => {
    const redirectAfterLogin = localStorage.getItem('redirectAfterLogin')
    if (redirectAfterLogin && redirectAfterLogin !== '/' && state.isAuthenticated) {
      return redirectAfterLogin
    }
    return '/orders'
  }

  return (
    <Routes>
      {/* Protected Routes */}
      <Route
        element={
          <RequireAuth>
            <DefaultLayout>
              <Outlet />
            </DefaultLayout>
          </RequireAuth>
        }
      >
        {(isRestrictedUser || isCertificateRequiredUser) && (
          <Route
            path="/"
            element={<Navigate to="/account/business-details" replace />}
          />
        )}
        {!isRestrictedUser && !isCertificateRequiredUser && (
          <Route
            path="/"
            element={<Navigate to={redirectAfterLogin()} replace />}
          />
        )}
        {!userFromPD3 && (
          <>
            <Route
              path="/account/"
              element={<Account page={'business-details'} />}
            />
            <Route
              path="/account/business-details"
              element={<Account page={'business-details'} />}
            />
            <Route
              path="/account/usage-policy"
              element={<Account page={'usage-policy'} />}
            />
            <Route
              path="/account/subscription"
              element={<Account page={'subscription'} />}
            />
            <Route path="/settings" element={<Settings />} />
            <Route
              path="/settings/change_password"
              element={<ChangePassword />}
            />
          </>
        )}
        {hasUserRights && (
          <>
            {!isRestrictedUser && !isCertificateRequiredUser && (
              <>
                <Route path="/application/care-giver" element={<CareGiver />} />

                <Route path="/application/assignment-codes">
                  <Route index element={<AssignmentCodes />} />
                  <Route
                    path=":assignmentCodeId"
                    element={<AssignmentCodeDetails />}
                  />
                  <Route
                    path=":assignmentCodeId/survey-report/:surveyTemplateID"
                    element={<SurveyTemplateReport />}
                  />
                </Route>

                <Route path="/orders">
                  <Route
                    index
                    element={
                      <Orders
                        showColumns={[
                          { key: 'created_at', label: 'Data utworzenia' },
                          { key: 'diet_start_date', label: 'Start diety' },
                          {
                            key: 'last_name' as keyof OrderExtendedProfile,
                            label: 'Imię i nazwisko'
                          },
                          { key: 'subscription', label: '' },
                          { key: 'status', label: 'Status' },
                          {
                            key: 'sale_document_created',
                            label: 'Dokument wydany'
                          },
                          { key: 'total_cost', label: 'Cena' },
                          { key: 'patient_profile', label: 'Przypisany kod' }
                        ]}
                      />
                    }
                  />
                  <Route path=":orderId" element={<OrderDetails />} />
                  <Route
                    path=":orderId/realization/"
                    element={<OrderRealization />}
                  />
                </Route>

                <Route path="/daymenus">
                  <Route index element={<DietBase />} />
                </Route>

                <Route path="/patient-profile">
                  <Route index element={<PatientProfilesList />} />
                  <Route path="create/" element={<CreatePatientProfile />} />
                  <Route
                    path=":patientProfileId"
                    element={<PatientProfileDetails />}
                  />
                  <Route
                    path=":patientProfileId/edit"
                    element={<CreatePatientProfile />}
                  />
                  <Route
                    path=":patientProfileId/survey/:patientSurveyId"
                    element={<PatientSurveyDetails />}
                  />
                  <Route
                    path=":patientProfileId/body-measurements/"
                    element={<PatientBodyMeasurementsDetails />}
                  />
                </Route>

                <Route path="/payments" element={<Payments />} />

                <Route
                  path="/payments/stripe-account"
                  element={<StripeAccount />}
                />
              </>
            )}

            {!isRestrictedUser &&
              !isCertificateRequiredUser &&
              stripeAccountCompleted && (
                <>
                  <Route path="/payments/balance" element={<Balance />} />
                  <Route
                    path="/payments/transactions"
                    element={<Transactions />}
                  />
                  <Route path="/payments/price-list" element={<PriceList />} />
                </>
              )}

            {!isRestrictedUser && !isCertificateRequiredUser && (
              <>
                <Route path="/payments/invoices" element={<Invoices />} />
              </>
            )}

            {hasMainUserRights && (
              <>
                <Route
                  path="/sub-accounts"
                  element={<SubAccounts userFromPD3={userFromPD3} />}
                />
                <Route path="/payments/statistics" element={<Statistics />} />
                <Route
                  path="/payments/invoices/report"
                  element={<EppReportPage />}
                />
              </>
            )}
          </>
        )}
      </Route>

      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <RedirectIfAuth>
            <Login errorsFromPD3={errorCode} />
          </RedirectIfAuth>
        }
      />

      {/* Fallback for unmatched routes */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}

export default App
